/**
 * The Sharing page (parts 38d, 38b; cockatrice-ui `Sharing.jsx`): the title and lead, the Show filter (Shared books,
 * Ending soon, All books), the four summary tiles, a card for each book (Manage and Share…, a table of the people it is
 * shared with and its open links: who, manuscript, notes, chapters, until when, in amber and in words when it ends this
 * week, the activity; Change and Remove for a person, Copy link and Revoke for a link), and under the ended access's
 * details End all reading links (LW3) and End all shared books (IW8; neither in the design, put where it ends access).
 * Opening it pulls every link's comments (table PL) and reads every share against the relay (DI8). A link or a person the
 * relay lists that this device holds no record of is counted as made on another device (LW4, IW4).
 * PLAN.md §3, step 5: built from the component library (Section, SegmentedControl, Card, Table with its phone cards,
 * Avatar, Badge, InlineConfirm, CopyField, Banner, EmptyState).
 */
import { createMemo, createSignal, onMount } from 'solid-js';
import type { Accessor, Signal } from 'solid-js';
import type { InviteRecord, ShareLinkRecord, ShareRecord } from '@cockatrice/storage';
import type { BookSummary } from '../shell/workspace.ts';
import { toBase64Url } from '../account/bytes.ts';
import { bytesOfHex } from '../share/secrets.ts';
import type { ListedLink } from '../share/wire.ts';
import { isOpen } from '../share/invite-wire.ts';
import type { OwnerInvite } from '../share/invite-wire.ts';
import { dateWords } from './account-sheet.ts';
import { Announcer, focusOn } from './announce.ts';
import { shownBook } from './chapter-list.ts';
import { el, each, when, whenSome } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import Avatar from '../ui/Avatar.ts';
import Badge from '../ui/Badge.ts';
import Banner from '../ui/Banner.ts';
import Button from '../ui/Button.ts';
import Card from '../ui/Card.ts';
import CopyField from '../ui/CopyField.ts';
import EmptyState from '../ui/EmptyState.ts';
import Icon from '../ui/Icon.ts';
import InlineConfirm from '../ui/InlineConfirm.ts';
import Section from '../ui/Section.ts';
import SegmentedControl from '../ui/SegmentedControl.ts';
import Table from '../ui/Table.ts';
import type { TableColumn } from '../ui/Table.ts';
import { component } from '../ui/solid.ts';
import { Header } from './home.ts';
import { CopyProblem, initialsOf, shownOf, standingOf } from './invite-dialog.ts';
import type { InviteStanding } from './invite-dialog.ts';
import type { Shell } from './shell.ts';

/** IW5: how far ahead an end counts as soon (amber, and "Ending soon"): this week. */
export const SOON_MS = 7 * 24 * 60 * 60 * 1000;

/** The days from `now` to `end`, rounded up: at least one. */
function daysTo(end: number, now: number): number {
  return Math.max(1, Math.ceil((end - now) / (24 * 60 * 60 * 1000)));
}

/** Whether `end` (an access's end, or none) comes this week from `now`. */
export function endsSoon(end: number | null, now: number): boolean {
  return end !== null && end > now && end - now <= SOON_MS;
}

/** The Show filter's choices (the design's). */
export type SharingFilter = 'shared' | 'soon' | 'all';

/** A person with access: their invite, its share, and where it stands. */
interface Person {
  readonly record: ShareRecord;
  readonly invite: InviteRecord;
  readonly standing: InviteStanding;
}

/** The relay's ID of `record`'s link: its bytes, base64url. */
function relayIdOf(record: ShareLinkRecord): string {
  return toBase64Url(bytesOfHex(record.id));
}

/** What the relay's list says of `record`, if it lists it. */
function listedOf(shell: Shell, record: ShareLinkRecord): ListedLink | undefined {
  const listed = shell.sharing.listed();
  return listed.kind === 'ok' ? listed.value.links.find((link) => link.id === relayIdOf(record)) : undefined;
}

/** A link's state as its row says it: paused by a cap, turned off by us, an upload left unfinished; else nothing. */
function stateWords(shell: Shell, record: ShareLinkRecord): string {
  const { t } = shell;
  const listed = listedOf(shell, record);
  const resets = shell.sharing.listed();
  const at = resets.kind === 'ok' ? resets.value.egress.resetsAt : shell.now();
  if (!record.uploaded) return t('sharing.uploading');
  if (listed?.cap === 'downloads') return t('sharing.paused.downloads', { date: dateWords(shell, at) });
  if (listed?.cap === 'egress') return t('sharing.paused.egress', { date: dateWords(shell, at) });
  return '';
}

/** How an ended link ended (`ended`, its record's), in words, with the operator's reason when it was turned off (decision 63). */
function endWords(shell: Shell, record: ShareLinkRecord, ended: NonNullable<ShareLinkRecord['ended']>): string {
  const { t } = shell;
  const words = t(`sharing.end.${ended.why}`, { date: dateWords(shell, ended.at) });
  const reason = listedOf(shell, record)?.disabled?.reason ?? null;
  const known = ['illegal', 'copyright', 'privacy', 'spam', 'other'] as const;
  const why = known.find((each) => each === reason);
  return why === undefined ? words : `${words}. ${t('sharing.disabledWhy', { reason: t(`sharing.reason.${why}`) })}`;
}

/** A person's activity as their row says it: where the invite stands, then (accepted) the notes written or the day. */
export function activityWords(shell: Pick<Shell, 't' | 'locale'>, standing: InviteStanding, known: OwnerInvite | undefined): string {
  const { t } = shell;
  if (standing === 'unsent') return t('sharing.person.unsent');
  if (standing === 'lost') return t('sharing.person.lost');
  if (known === undefined) return standing === 'pending' ? t('sharing.person.pending') : t('sharing.person.accepted');
  if (known.noteCount > 0) return t('sharing.person.notes', { count: known.noteCount });
  return known.claimedAt === null ? t('sharing.person.pending') : t('sharing.person.acceptedOn', { date: dateWords(shell, known.claimedAt) });
}

/** Until when an access lasts (`end`, the day after its last); this week, the days left beside it in amber (1.4.1: in words too). */
function untilWords(shell: Shell, end: number | null): Child {
  const { t } = shell;
  if (end === null) return t('sharing.noEnd');
  const date = dateWords(shell, end - 1);
  // One piece, so a phone's card line keeps the date and its days together.
  return endsSoon(end, shell.now()) ? el('span', {}, date, ' · ', component(Badge, { tone: 'warn', class: 'ends-soon', children: t('sharing.inDays', { count: daysTo(end, shell.now()) }) })) : date;
}

/** Who a row is for, beside its mark: a name, and its kind (role, or a reading link) under it. */
function Who(mark: Child, name: string, kind: string, extra: Child = null): Child {
  return el('span', { class: 'sh-who' }, mark, el('span', { class: 'sh-grow' }, el('strong', { class: 'sh-break' }, name), el('span', { class: 'sh-help sh-block' }, kind), extra));
}

/** A row of a book's table: a person with access, or an open reading link (its link shown when the clipboard won't take it). */
type AccessRow = { readonly kind: 'person'; readonly person: Person } | { readonly kind: 'link'; readonly record: ShareLinkRecord; readonly shown: Signal<string | null> };

/** One open link's actions: Copy link, and Revoke asked first in place (3.3.4), saying what revoking ends. */
function LinkActions(shell: Shell, row: Extract<AccessRow, { kind: 'link' }>, say: (text: string) => void): Child {
  const { t, sharing } = shell;
  const { record } = row;
  const name = record.name;
  let busy = false;
  const copy = async (): Promise<void> => {
    const url = sharing.urlNow(record);
    if (url === undefined) {
      say(t('share.copyFailed'));
      return;
    }
    const done = await shell.copy(url);
    // Deep-fix r5, CU: the link written in the press; shown in a field to select when the clipboard won't take it.
    if (!done) row.shown[1](url);
    say(done ? t('sharing.copied', { name }) : t('share.copyFailed'));
  };
  const revoke = async (): Promise<void> => {
    // The question goes as it is answered: a second answer meanwhile sends nothing more.
    if (busy) return;
    busy = true;
    const revoked = await sharing.revoke(record);
    busy = false;
    say(revoked.kind === 'revoked' ? t('sharing.revoked', { name }) : t('sharing.revokeFailed', { name }));
    focusOn(shell, revoked.kind === 'revoked' ? '#sharing-title' : `[data-link="${record.id}"] .revoke-link`);
  };
  return [
    component(Button, { size: 'sm', class: 'copy-link', 'aria-label': t('sharing.copyLinkLabel', { name }), onClick: () => void copy(), children: t('sharing.copyLink') }),
    component(InlineConfirm, {
      label: t('sharing.revoke'),
      triggerLabel: t('sharing.revokeLabel', { name }),
      message: t('sharing.revokeAsk', { name }),
      cancelLabel: t('sharing.keep'),
      confirmLabel: t('sharing.revokeConfirm'),
      triggerClass: 'revoke-link ui-inline-confirm__trigger ui-inline-confirm__trigger--danger',
      onConfirm: () => void revoke(),
    }),
  ];
}

/** A person's actions: Change (their invite in the book's dialog), and Remove asked first in place (3.3.4). */
function PersonActions(shell: Shell, person: Person, say: (text: string) => void): Child {
  const { t, sharing, workspace, ui } = shell;
  const { invite, record } = person;
  const email = invite.email;
  let busy = false;
  const change = async (): Promise<void> => {
    await workspace.openBook(record.bookId);
    shell.go('write');
    ui.setDialog({ kind: 'invite', person: invite.id });
  };
  const remove = async (): Promise<void> => {
    if (busy) return;
    busy = true;
    const removed = await sharing.remove(record.bookId, invite.id);
    busy = false;
    say(removed.kind === 'done' ? t('invite.removed', { email }) : t('sharing.removeFailed', { email }));
    focusOn(shell, removed.kind === 'done' ? '#sharing-title' : `[data-person="${invite.id}"] .remove-person`);
  };
  return [
    component(Button, { size: 'sm', class: 'change-person', 'aria-label': t('sharing.changeLabel', { email }), onClick: () => void change(), children: t('sharing.change') }),
    component(InlineConfirm, {
      label: t('sharing.remove'),
      triggerLabel: t('sharing.removeLabel', { email }),
      message: t('invite.removeAsk', { email }),
      cancelLabel: t('sharing.keep'),
      confirmLabel: t('invite.removeConfirm'),
      triggerClass: 'remove-person ui-inline-confirm__trigger ui-inline-confirm__trigger--danger',
      onConfirm: () => void remove(),
    }),
  ];
}

/** A row's "who": a person's mark (initials in their role's colour once accepted, "@" before), or a link's. */
function WhoCell(shell: Shell, row: AccessRow): Child {
  const { t } = shell;
  if (row.kind === 'link') {
    const { record, shown } = row;
    return Who(
      component(Avatar, { tone: 'link', children: component(Icon, { name: 'link', size: 'sm' }) }),
      record.name,
      t('sharing.kind'),
      whenSome(shown[0], (url) =>
        component(CopyField, {
          class: 'shown-link',
          get value() {
            return url();
          },
          label: t('sharing.linkOf', { name: record.name }),
          hideLabel: true,
          // The app's copier; when it can't, the field stays to select (the row has said so once already).
          copy: (value: string) => shell.copy(value),
        }),
      ),
    );
  }
  const { invite, standing } = row.person;
  const accepted = standing === 'accepted';
  return Who(
    component(Avatar, { tone: accepted ? (invite.role === 'beta-reader' ? 'reader' : 'editor') : 'neutral', class: accepted ? undefined : 'person-pending', children: accepted ? initialsOf(invite.email) : '@' }),
    invite.email,
    t(`invite.roleName.${invite.role}`),
  );
}

/** A book's card: its title and summary, Manage and Share…, and the table of its people and open links (cards on a phone). */
function BookAccess(shell: Shell, book: BookSummary, people: Accessor<readonly Person[]>, links: Accessor<readonly ShareLinkRecord[]>, say: (text: string) => void): Child {
  // Each row by its ID (and a link's upload), so a row (a Remove asked) outlives the records read again under it.
  const linkRows = createMemo(() => links(), [], { equals: (a, b) => a.length === b.length && a.every((record, at) => record.id === b[at]?.id && record.uploaded === b[at].uploaded) });
  // CS″: each person kept while what their row shows is the same (a list read again builds the shown invite anew).
  const shownKey = (person: Person): string => JSON.stringify([person.invite.id, person.invite.role, person.invite.notes, person.invite.export, person.invite.endsAt, person.invite.chapters, person.standing]);
  const personRows = createMemo(() => people(), [], {
    equals: (a, b) => {
      const keys = b.map(shownKey);
      return a.length === b.length && a.every((person, at) => shownKey(person) === keys[at]);
    },
  });
  // One row object per person and link kept, so the table keeps its row while the other kind is read again.
  const personRow = new WeakMap<Person, AccessRow>();
  const linkRow = new WeakMap<ShareLinkRecord, AccessRow>();
  const rows = createMemo((): readonly AccessRow[] => [
    ...personRows().map((person) => {
      const made = personRow.get(person) ?? { kind: 'person', person };
      personRow.set(person, made);
      return made;
    }),
    ...linkRows().map((record) => {
      const made = linkRow.get(record) ?? { kind: 'link', record, shown: createSignal<string | null>(null) };
      linkRow.set(record, made);
      return made;
    }),
  ]);
  const { t, workspace, ui, sharing } = shell;
  const title = shownBook(shell, book);
  const open = async (dialog: 'share' | 'invite'): Promise<void> => {
    await workspace.openBook(book.id);
    shell.go('write');
    ui.setDialog(dialog === 'share' ? { kind: 'share' } : { kind: 'invite', person: null });
  };
  const summary = (): string => {
    const parts = [];
    if (people().length > 0) parts.push(t('sharing.people', { count: people().length }));
    if (links().length > 0) parts.push(t('sharing.bookSummary', { count: links().length }));
    return parts.length === 0 ? t('sharing.notShared') : parts.join(' · ');
  };
  const text = (words: (row: AccessRow) => string) => (row: AccessRow): Child => words(row);
  const columns: readonly TableColumn<AccessRow>[] = [
    { key: 'who', header: t('sharing.who'), render: (row) => WhoCell(shell, row) },
    { key: 'manuscript', header: t('sharing.manuscript'), render: text(() => t('invite.read')) },
    { key: 'notes', header: t('sharing.notes'), render: text((row) => (row.kind === 'link' ? (row.record.comments ? t('sharing.comment') : t('invite.notes.none')) : t(`invite.notes.${row.person.invite.notes}`))) },
    {
      key: 'chapters',
      header: t('sharing.chapters'),
      render: text((row) =>
        row.kind === 'link'
          ? t('sharing.copyFrom', { chapters: t('sharing.chapterCount', { count: row.record.chapters.length }), date: dateWords(shell, row.record.createdAt) })
          : row.person.invite.chapters === null
            ? t('sharing.all')
            : t('sharing.chosen'),
      ),
    },
    {
      key: 'until',
      header: t('sharing.until'),
      render: (row) =>
        row.kind === 'person'
          ? untilWords(shell, row.person.invite.endsAt)
          : [
              untilWords(shell, row.record.expiresAt),
              // A link paused by a cap, or its upload left unfinished, said with the warning mark.
              () => {
                const state = stateWords(shell, row.record);
                return state === '' ? null : el('span', { class: 'sh-help sh-block link-state' }, component(Icon, { name: 'warn', size: 'sm' }), ' ', state);
              },
            ],
    },
    {
      key: 'activity',
      header: t('sharing.activity'),
      render: (row) =>
        row.kind === 'link'
          ? () => {
              const count = sharing.comments().get(row.record.id) ?? 0;
              return count === 0 ? t('sharing.noComments') : t('sharing.comments', { count });
            }
          : () => activityWords(shell, row.person.standing, sharing.listedOf(row.person.record, row.person.invite)),
    },
  ];
  return component(Card, {
    class: 'sh-bookcard',
    role: 'region',
    'aria-label': title,
    titleAs: 'h2',
    title,
    meta: summary,
    actions: [
      when(
        () => sharing.invitesAvailable(),
        () => component(Button, { size: 'sm', class: 'manage-book', 'aria-label': t('sharing.manageLabel', { title }), onClick: () => void open('invite'), children: t('sharing.manage') }),
      ),
      when(
        () => sharing.available(),
        () => component(Button, { variant: 'primary', size: 'sm', class: 'share-book', 'aria-label': t('sharing.shareLabel', { title }), onClick: () => void open('share'), children: t('sharing.share') }),
      ),
    ],
    children: [
      CopyProblem(shell, () => book.id),
      when(
        () => rows().length === 0,
        () => el('p', { class: 'sh-only-you' }, t('sharing.onlyYou')),
        () =>
          component(Table<AccessRow>, {
            class: 'sh-table',
            label: title,
            columns,
            get rows() {
              return rows();
            },
            rowAttrs: (row) => (row.kind === 'link' ? { 'data-link': row.record.id } : { 'data-person': row.person.invite.id }),
            actionsHeader: el('span', { class: 'visually-hidden' }, t('sharing.actions')),
            actions: (row) => (row.kind === 'link' ? LinkActions(shell, row, say) : PersonActions(shell, row.person, say)),
          }),
      ),
    ],
  });
}

/** LW3 and IW8: an End all, under its help, asked first in place; what it did said once. */
function EndAll(shell: Shell, words: { readonly hook: string; readonly label: string; readonly help: string; readonly ask: string; readonly confirm: string }, end: () => Promise<string>, say: (text: string) => void): Child {
  let busy = false;
  const go = async (): Promise<void> => {
    if (busy) return;
    busy = true;
    say(await end());
    busy = false;
    focusOn(shell, `.${words.hook}`);
  };
  return el(
    'div',
    { class: 'sh-end-all' },
    el('p', { class: 'sh-help', id: `${words.hook}-help` }, words.help),
    component(InlineConfirm, {
      label: words.label,
      message: words.ask,
      cancelLabel: shell.t('sharing.keep'),
      confirmLabel: words.confirm,
      triggerDescribedBy: `${words.hook}-help`,
      triggerClass: `${words.hook} ui-inline-confirm__trigger ui-inline-confirm__trigger--danger`,
      onConfirm: () => void go(),
    }),
  );
}

/** The Sharing page. */
export function SharingPage(shell: Shell, statuses: () => Child): Child {
  const { t, sharing, workspace } = shell;
  onMount(() => {
    void sharing.load();
  });
  // A13: what an action did, said once (the same words again too: cleared first).
  const [news, setNews] = createSignal('');
  const say = (text: string): void => {
    setNews('');
    setNews(text);
  };
  const [filter, setFilter] = createSignal<SharingFilter>('shared');
  const records = createMemo(() => {
    const read = sharing.records();
    return read.kind === 'ok' ? read.value : [];
  });
  const open = createMemo(() => records().filter((record) => record.ended === null));
  const ended = createMemo(() => records().flatMap((record) => (record.ended === null ? [] : [{ record, end: record.ended }])));
  // Part 38b: the people with access (open shares' invites not over), and those whose access ended.
  // The share records as last read (none before the first read).
  const shares = createMemo(() => {
    const read = sharing.shares();
    return read.kind === 'ok' ? read.value : [];
  });
  const everyone = createMemo(() =>
    shares().flatMap((record) =>
      record.invites.map((invite): Person => {
        // CS″: shown as the relay holds it (its role, notes, export, end), the record's own when unsent.
        const listed = sharing.listedOf(record, invite);
        return { record, invite: shownOf(invite, listed), standing: record.ended === null ? standingOf(invite, listed, shell.now()) : 'ended' };
      }),
    ),
  );
  const people = createMemo(() => everyone().filter((person) => person.standing !== 'ended'));
  const gone = createMemo(() => everyone().filter((person) => person.standing === 'ended'));
  // Every open access's end, by book: the people's and the links'.
  const ends = createMemo(() => {
    const byBook = new Map<string, (number | null)[]>();
    const add = (bookId: string, end: number | null): void => {
      byBook.set(bookId, [...(byBook.get(bookId) ?? []), end]);
    };
    for (const person of people()) add(person.record.bookId, person.invite.endsAt);
    for (const record of open()) add(record.bookId, record.expiresAt);
    return byBook;
  });
  const soon = (bookId: string): boolean => (ends().get(bookId) ?? []).some((end) => endsSoon(end, shell.now()));
  const shared = (bookId: string): boolean => people().some((person) => person.record.bookId === bookId) || open().some((record) => record.bookId === bookId);
  const books = createMemo(() => {
    const which = filter();
    return workspace.books().filter((book) => (which === 'all' ? true : which === 'soon' ? soon(book.id) : shared(book.id)));
  });
  const elsewhere = createMemo(() => {
    const listed = sharing.listed();
    if (listed.kind !== 'ok') return 0;
    const held = new Set(records().map(relayIdOf));
    return listed.value.links.filter((link) => !held.has(link.id) && (link.state === 'live' || link.state === 'uploading')).length;
  });
  // IW4: people the account's shares hold that this device holds no record of.
  const elsewherePeople = createMemo(() => {
    const listed = sharing.accountShares();
    if (listed.kind !== 'ok') return 0;
    const held = new Set(shares().map((record) => toBase64Url(bytesOfHex(record.id))));
    return listed.value.filter((share) => !held.has(share.id) && share.endedAt === null).reduce((count, share) => count + share.invites.filter((invite) => isOpen(invite.state)).length, 0);
  });
  const sharedBooks = createMemo(() => workspace.books().filter((book) => shared(book.id)).length);
  const endingSoon = createMemo(() => people().filter((person) => endsSoon(person.invite.endsAt, shell.now())).length + open().filter((record) => endsSoon(record.expiresAt, shell.now())).length);
  // The design's tiles: the number big, its words under it (one or more); the ending one with the warning mark once any end.
  const tile = (count: Accessor<number>, words: (count: number) => string, warn = false): Child =>
    el(
      'li',
      {},
      component(Card, {
        padding: 'sm',
        titleAs: 'div',
        get class() {
          return warn && count() > 0 ? 'sh-tile sh-tile--warn' : 'sh-tile';
        },
        get tone() {
          return warn && count() > 0 ? 'warn' : 'default';
        },
        title: () => String(count()),
        meta: () => words(count()),
      }),
    );
  return el(
    'div',
    { class: 'home sh-page' },
    Header(shell, 'sharing'),
    statuses(),
    Announcer('sharing', news),
    el(
      'main',
      { class: 'sh-page__main' },
      component(Section, {
        level: 1,
        size: 'lg',
        title: t('sharing.title'),
        headingId: 'sharing-title',
        headingFocusable: true,
        description: t('sharing.lead'),
        actions: component(SegmentedControl, {
          label: t('sharing.show'),
          class: 'sharing-filter',
          get fullWidth() {
            return shell.mode() === 'narrow';
          },
          options: [
            { value: 'shared', label: t('sharing.filter.shared') },
            { value: 'soon', label: t('sharing.filter.soon') },
            { value: 'all', label: t('sharing.filter.all') },
          ],
          get value() {
            return filter();
          },
          onChange: (value: string) => {
            setFilter(value === 'soon' || value === 'all' ? value : 'shared');
          },
        }),
        children: el(
          'div',
          { class: 'sh-page__body' },
          // The loading words, and the list failing with Try again (a live region from the start, said once).
          el(
            'div',
            { class: 'sh-status', role: 'status' },
            () => {
              const read = sharing.records();
              const listed = sharing.listed();
              if (read.kind === 'loading') return t('sharing.loading');
              return listed.kind === 'failed'
                ? component(Banner, { tone: 'warn', actions: component(Button, { size: 'sm', class: 'sharing-retry', onClick: () => void sharing.load(), children: t('sharing.retry') }), children: t('sharing.failed') })
                : '';
            },
          ),
          el(
            'ul',
            { class: 'sh-tiles', 'aria-label': t('sharing.summaryLabel') },
            tile(sharedBooks, (count) => (count === 1 ? t('sharing.tileBook') : t('sharing.tileBooks'))),
            tile(() => people().length, (count) => (count === 1 ? t('sharing.tilePerson') : t('sharing.tilePeople'))),
            tile(() => open().length, (count) => (count === 1 ? t('sharing.tileLink') : t('sharing.tileLinks'))),
            tile(endingSoon, (count) => (count === 1 ? t('sharing.tileSoon') : t('sharing.tileSoons')), true),
          ),
          when(
            () => (sharing.records().kind === 'ok' || sharing.shares().kind === 'ok') && books().length === 0,
            () =>
              component(EmptyState, {
                size: 'inline',
                icon: 'people',
                headingLevel: 2,
                class: 'sharing-none',
                get title() {
                  return filter() === 'soon' ? t('sharing.noneSoon') : t('sharing.noneTitle');
                },
                get children() {
                  return filter() === 'soon' ? undefined : t('sharing.none');
                },
              }),
          ),
          each(books, (book) =>
            BookAccess(
              shell,
              book,
              () => people().filter((person) => person.record.bookId === book.id),
              () => open().filter((record) => record.bookId === book.id),
              say,
            ),
          ),
          when(
            () => elsewhere() > 0,
            () => component(Banner, { tone: 'info', class: 'sh-elsewhere', title: () => t('sharing.elsewhere', { count: elsewhere() }), children: t('sharing.elsewhereHelp') }),
          ),
          when(
            () => elsewherePeople() > 0,
            () => component(Banner, { tone: 'info', class: 'sh-elsewhere-people', title: () => t('sharing.elsewherePeople', { count: elsewherePeople() }), children: t('sharing.elsewherePeopleHelp') }),
          ),
          el(
            'details',
            { class: 'sh-history' },
            el('summary', {}, () => t('sharing.ended', { count: ended().length + gone().length })),
            el('p', { class: 'sh-help' }, t('sharing.endedHelp')),
            whenSome(
              () => (ended().length + gone().length === 0 ? null : true),
              () =>
                el(
                  'ul',
                  { class: 'sh-ended' },
                  each(gone, ({ invite }) => el('li', {}, el('strong', {}, invite.email), ' · ', t('sharing.personEnded'))),
                  each(ended, ({ record, end }) => el('li', {}, el('strong', {}, record.name), ' · ', () => endWords(shell, record, end))),
                ),
            ),
            when(
              () => sharing.available(),
              () =>
                EndAll(
                  shell,
                  { hook: 'end-all', label: t('sharing.endAll'), help: t('sharing.endAllHelp'), ask: t('sharing.endAllAsk'), confirm: t('sharing.endAllConfirm') },
                  async () => {
                    const count = await sharing.revokeAll();
                    return count === null ? t('sharing.endAllFailed') : t('sharing.endedAll', { count });
                  },
                  say,
                ),
            ),
            when(
              () => sharing.invitesAvailable(),
              () =>
                EndAll(
                  shell,
                  { hook: 'end-all-shares', label: t('sharing.endAllShares'), help: t('sharing.endAllSharesHelp'), ask: t('sharing.endAllSharesAsk'), confirm: t('sharing.endAllSharesConfirm') },
                  async () => {
                    const count = await sharing.endAllShares();
                    return count === null ? t('sharing.endAllFailed') : t('sharing.endedAllShares', { count });
                  },
                  say,
                ),
            ),
          ),
        ),
      }),
    ),
  );
}
